



























































































































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import smoothReflow from 'vue-smooth-reflow';
import { TransitionExpand } from 'vue-transition-expand';
import OrderPaymentTransaction, { OrderPaymentTransactionStatus as PaymentStatus,
  OrderPaymentTransactionType as PaymentType } from '@/zenky/interfaces/orders/payments/Transaction';
import OrderPaymentsSummary from '@/zenky/interfaces/orders/payments/Summary';
import { CreatePaymentTransaction, RefundPaymentTransaction } from '@/zenky/interfaces/orders/payments/PostRequests';
import DateTime from '@/zenky/interfaces/common/DateTime';
import { randomId } from '@/helpers';
import SelectField from '../common/SelectField.vue';
import TweeningNumber from '@/components/forms/fields/TweeningNumber';
import FormFieldOption from '@/zenky/forms/interfaces/FormFieldOption';
import PaymentMethod from '@/zenky/interfaces/orders/PaymentMethod';
import axios from '@/zenky/api/Http';
import { DeleteIcon } from 'vue-feather-icons';
import { getZeroPriceStub, formatPriceRub } from '@/helpers/utils';

const paymentMethodOptions: (FormFieldOption & { value: PaymentMethod, label: string })[] = [
  { value: PaymentMethod.CASH, label: 'Наличные' },
  { value: PaymentMethod.CREDIT_CARD, label: 'Картой курьеру' },
  // Not available for now { value: PaymentMethod.ONLINE, label: 'Картой онлайн' },
];

type DraftPaymentTransaction = {
  id: OrderPaymentTransaction['id'];
  method: Pick<OrderPaymentTransaction['method'], 'id'>;
  amount: number;
  // Only applicable for cash payments, but not optional so we don't have to `Vue.set(...)` if we change the payment
  // method.
  takeAmount: number;
}
function isDraftPayment(
  payment: DraftPaymentTransaction | OrderPaymentTransaction,
): payment is DraftPaymentTransaction {
  const key: keyof OrderPaymentTransaction = 'created_at';
  return !(key in payment);
}

export type CreatePaymentEvent = CreatePaymentTransaction;

type AsyncEvent<P, S = (value?: unknown) => void, J = (reason?: any) => void> = [P, S, J]
type PinAuthAsyncEvent<P> = AsyncEvent<P, () => void, (reason: 'incorrect-pin' | Error) => void>;

type RefundEventPayload = RefundPaymentTransaction & { paymentId: string, pin: string };
export type RefundEvent = PinAuthAsyncEvent<RefundEventPayload>;

interface CancelPendingRefundEventPayload {
  paymentTransactionId: OrderPaymentTransaction['id'];
  pin: string;
}
export type CancelPendingRefundEvent = PinAuthAsyncEvent<CancelPendingRefundEventPayload>;

function parsePaymentAmount(str: string): number | null {
  if (str.length === 0) {
    return 0;
  }
  const newVal = parseInt(str, 10);
  if (!Number.isSafeInteger(newVal) || newVal < 0) {
    return null;
  }
  return newVal;
}

type DialogDataBase = {
  type: 'refund' | 'cancelPendingRefund';
  payment: OrderPaymentTransaction;
  pin: string;
  pinInvalid: boolean;
};
type RefundDialogData = DialogDataBase & {
  type: 'refund';
  amount: number;
}
type CancelPendingRefundDialogData = DialogDataBase & { type: 'cancelPendingRefund' }

@Component({
  components: { SelectField, DeleteIcon, TweeningNumber, TransitionExpand },
  mixins: [smoothReflow],
})
export default class PaymentSection extends Vue {
  @Prop({ required: true, type: Array }) readonly payments!: OrderPaymentTransaction[];
  @Prop({ required: true, type: Object }) readonly paymentsSummary!: OrderPaymentsSummary;
  private readonly sectionUid = randomId('order-sction');
  private readonly paymentMethodOptions = paymentMethodOptions;
  private readonly PaymentMethodEnum = PaymentMethod;
  private readonly PaymentType = PaymentType;
  private readonly PaymentStatus = PaymentStatus;
  private readonly isDraftPayment = isDraftPayment;
  draftPayments: DraftPaymentTransaction[] = [];
  dialog: CancelPendingRefundDialogData | RefundDialogData | null = null; // `null` when it is closed.

  mounted() {
    const refsToSmooth = ['receivedPayments', 'refunds', 'awaitedPayments', 'addDraftPaymentButtonWrapper'];
    refsToSmooth.forEach(ref => this.$smoothReflow!({ el: this.$refs[ref] as Element, transition: 'height 0.3s' }));
  }

  get isPaid(): boolean {
    return this.paymentsSummary.amount_to_confirm.value === 0 && this.paymentsSummary.amount_to_pay.value === 0;
  }
  get receivedPayments(): this['payments'] {
    return this.payments.filter(payment => (
      payment.type.id === PaymentType.PAYMENT
      && payment.status.id !== PaymentStatus.PENDING
      && payment.status.id !== PaymentStatus.CANCELLED
    ));
  }
  get refunds(): this['payments'] {
    return this.payments.filter(payment => payment.type.id === PaymentType.REFUND);
  }
  get awaitedPayments(): (DraftPaymentTransaction | this['payments'][number])[] {
    return [
      ...this.payments.filter(payment => (
        payment.type.id === PaymentType.PAYMENT
        && payment.status.id === PaymentStatus.PENDING
      )),
      ...this.draftPayments,
    ];
  }
  getDraftPaymentChange(payment: DraftPaymentTransaction) {
    return payment.takeAmount - payment.amount;
  }
  getPaymentTakeAmount(payment: OrderPaymentTransaction) {
    if (payment.method.id !== PaymentMethod.CASH) {
      throw new Error('This method is only applicable for cash payments');
    }
    // TODO `/100`? Is this always try? Don't we get already calculated value from the backend?
    return (payment.amount.value + payment.meta.change!.value) / 100;
  }
  addDraftPayment() {
    const zeroPriceStub = getZeroPriceStub();
    this.draftPayments.push({
      id: `draft-${(new Date()).toUTCString()}-${Math.random().toString()}`,
      method: { id: PaymentMethod.CASH },
      amount: 0,
      takeAmount: 0,
    });
  }
  removeDraftPayment(payment: DraftPaymentTransaction) {
    this.draftPayments.splice(this.draftPayments.indexOf(payment));
  }
  createPayment(payment: DraftPaymentTransaction) {
    const payload: CreatePaymentTransaction = {
      method: payment.method.id,
      // TODO Don't just ignore `payment.amount` if it's cash.
      amount: payment.method.id === PaymentMethod.CASH ? payment.takeAmount : payment.amount,
      confirmed: false,
    };
    this.$emit('create', payload);
    this.removeDraftPayment(payment);
  }
  onCostChange(payment: DraftPaymentTransaction, $event: Event) {
    const parsed = parsePaymentAmount(($event.target as HTMLInputElement).value);
    if (parsed === null) {
      return;
    }
    payment.amount = parsed; // eslint-disable-line no-param-reassign
    if (payment.takeAmount < payment.amount) {
      payment.takeAmount = payment.amount; // eslint-disable-line no-param-reassign
    }
  }
  onTakeAmountChange(payment: DraftPaymentTransaction, $event: Event) {
    const parsed = parsePaymentAmount(($event.target as HTMLInputElement).value);
    if (parsed === null) {
      return;
    }
    if (parsed < payment.amount) {
      // TODO show validation error instead?
      payment.takeAmount = payment.amount; // eslint-disable-line no-param-reassign
    } else {
      payment.takeAmount = parsed; // eslint-disable-line no-param-reassign
    }
  }
  selectInputText($event: Event) {
    // `$nextTick` because by default clicking on an input discards selection and moves (places) the cursor.
    this.$nextTick(() => ($event.target as HTMLInputElement).select());
  }

  // eslint-disable is a workaround to make sure switch-case is exhaustive. TODO?
  // eslint-disable-next-line getter-return
  get dialogInferredData(): ({
    onAccept: PaymentSection['refund'] | PaymentSection['cancelPendingRefund'] | (() => never);
    title: string;
    acceptText: string;
    cancelText: string;
    class: string;
  }) {
    if (this.dialog === null) {
      return {
        onAccept: () => { throw new Error('Accepted the dialog while its data is `null`'); },
        title: '',
        acceptText: '',
        cancelText: '',
        class: '',
      };
    }
    switch (this.dialog.type) { // eslint-disable-line default-case
      case 'refund': return {
        onAccept: this.refund,
        title: 'Возврат платежа',
        acceptText: 'Вернуть',
        cancelText: 'Отменить',
        class: 'refund',
      };
      case 'cancelPendingRefund': return {
        onAccept: this.cancelPendingRefund,
        title: 'Отмена намеченного возврата',
        acceptText: 'Отменить возврат',
        cancelText: 'Оставить',
        class: 'cancel-pending-refund',
      };
    }
  }
  openRefundDialog(payment: OrderPaymentTransaction) {
    if (payment.status.id !== PaymentStatus.CONFIRMED) {
      throw new Error(`Refunds can only be initiated for payments with "${PaymentStatus.CONFIRMED}" status. \
        Got "${payment.status.id}"`);
    }
    this.dialog = {
      type: 'refund',
      payment,
      amount: parseInt(payment.amount.trimmed, 10),
      pin: '',
      pinInvalid: false,
    };
  }
  async refund() {
    if (this.dialog === null) { throw new Error('Dialog data must be set first'); }
    if (this.dialog.type !== 'refund') {
      throw new Error(`Called "refund" while the dialog type is "${this.dialog.type}"`);
    }
    const payload: RefundEventPayload = {
      paymentId: this.dialog.payment.id,
      amount: this.dialog.amount,
      pin: this.dialog.pin,
    };
    try {
      await new Promise((resolve, reject) => {
        this.$emit('refund', payload, resolve, reject);
      });
    } catch (err) {
      if (err as Parameters<PinAuthAsyncEvent<RefundEventPayload>[2]>[0] !== 'incorrect-pin') {
        throw err;
      }
      this.dialog.pinInvalid = true;
      return;
    }
    this.dialog = null;
  }
  openCancelPendingRefundDialog(payment: OrderPaymentTransaction) {
    if (payment.status.id !== PaymentStatus.PENDING_REFUND) {
      throw new Error(`Refunds can only be cancelled for payments with "${PaymentStatus.PENDING_REFUND}" status. \
        Got "${payment.status.id}"`);
    }
    this.dialog = {
      type: 'cancelPendingRefund',
      payment,
      pin: '',
      pinInvalid: false,
    };
  }
  async cancelPendingRefund() {
    if (this.dialog === null) { throw new Error('Dialog data must be set first'); }
    if (this.dialog.type !== 'cancelPendingRefund') {
      throw new Error(`Called "cancelPendingRefund" while the dialog type is "${this.dialog.type}"`);
    }
    const payload: CancelPendingRefundEventPayload = {
      paymentTransactionId: this.dialog.payment.id,
      pin: this.dialog.pin,
    };

    try {
      await new Promise((resolve, reject) => {
        this.$emit('cancel-pending-refund', payload, resolve, reject);
      });
    } catch (err) {
      if (err as Parameters<PinAuthAsyncEvent<CancelPendingRefundEventPayload>[2]>[0] !== 'incorrect-pin') {
        throw err;
      }
      this.dialog.pinInvalid = true;
      return;
    }
    this.dialog = null;
  }

  private get formatPriceRub() { return formatPriceRub; }
}

interface PaymentTransactionRowProps {
  transaction: OrderPaymentTransaction;
}
const PaymentTransactionRow = Vue.component<PaymentTransactionRowProps>('PaymentTransactionRow', {
  functional: true,
  props: {
    transaction: {
      type: Object,
      required: true,
    },
  },
  render: (createElement, context) => {
    function getRelevantDatetime(payment: OrderPaymentTransaction): DateTime {
      let datetime: DateTime;
      switch (payment.status.id) {
        case PaymentStatus.PENDING: return payment.created_at;
        case PaymentStatus.CONFIRMED: return payment.confirmed_at!;
        case PaymentStatus.CANCELLED: return payment.cancelled_at!;
        default: {
          if (payment.type.id !== PaymentType.PAYMENT) {
            throw new Error(`Payment status ${payment.status.id} is only applicable for payments of type \
              ${PaymentType.PAYMENT}`);
          }
          switch (payment.status.id) {
            case PaymentStatus.PENDING_REFUND: return payment.confirmed_at!;
            case PaymentStatus.REFUNDING: return payment.confirmed_at!;
            case PaymentStatus.REFUND_FAILED: return payment.refund_failed_at!;
            case PaymentStatus.REFUNDED: return payment.refunded_at!;
            default: throw new Error(`Unknown payment status ${payment.status.id}`);
          }
        }
      }
    }
    function getTimeline(payment: OrderPaymentTransaction) {
      const allEventPropKeys: [
          'created_at', 'confirmed_at', 'cancelled_at', 'refunded_at', 'refund_failed_at',
        ] = ['created_at', 'confirmed_at', 'cancelled_at', 'refunded_at', 'refund_failed_at']; // No `updated_at`.
      const availableEventPropKeys = allEventPropKeys.filter(key => payment[key] !== null);
      const availableEvents = availableEventPropKeys.map(key => ({ name: key, datetime: payment[key]! }));
      const sortedEvents = availableEvents.sort(
        (a, b) => Date.parse(a.datetime.datetime_utc) - Date.parse(b.datetime.datetime_utc),
      );
      return sortedEvents;
    }
    const { transaction } = context.props;
    const timelineText = getTimeline(transaction).reduce((acc, event) => {
      let label: string;
      switch (event.name) {
        case 'created_at': label = 'создание'; break;
        case 'confirmed_at': label = 'подтверждение'; break;
        case 'cancelled_at': label = 'отмена'; break;
        case 'refunded_at': label = 'возврат'; break;
        case 'refund_failed_at': label = 'ошибка возврата'; break;
        default: throw new Error(`Unknown datetime key ${event.name}`);
      }
      const newLine = `${event.datetime.datetime_at} – ${label}`;
      return `${acc}${newLine}\n`;
    }, '');
    const datetime = getRelevantDatetime(transaction);
    const date = datetime.datetime_at.slice(0, 10);
    const time = datetime.datetime_at.slice(-5);
    return createElement('tr', [
      createElement('td', { class: 'method' }, transaction.method.name),
      createElement('td', { class: 'amount' }, transaction.amount.full),
      createElement('td', { class: 'date' }, date),
      createElement(
        'td',
        {
          class: 'time',
          attrs: { title: timelineText },
        },
        time,
      ),
      context.slots().default,
    ]);
  },
});
